import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useCheckerAccount } from "@/api/checkers";
import { api, errorMessage, unwrap } from "@/api/client";
import { type ProviderOffer, usd, useProviderAccounts, useProviderBalance, useProviderOffers } from "@/api/providers";
import { RUNS_KEY } from "@/api/runs";
import { CheckRow, Field, NumberRow } from "@/components/form-rows";
import { List, ListHeader, ListItem, ListItemContent, ListItemTrailing, ListTitle } from "@/components/list";
import Loader from "@/components/loader";
import Text from "@/components/text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Combobox } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/utils/cn";

type Knobs = {
  concurrency: number;
  max_attempts: number;
  failure_limit: number;
  stagger_seconds: number;
  send_rate_per_second: number;
  otp_poll_timeout: number;
  auto_email: boolean;
};

// A code sent to a bought number can take a while, so the wait is longer than for a
// pasted list.
const KNOBS: Knobs = {
  concurrency: 3,
  max_attempts: 2,
  failure_limit: 10,
  stagger_seconds: 2,
  send_rate_per_second: 1,
  otp_poll_timeout: 90,
  auto_email: true,
};

type Country = { code: string; name: string; offers: ProviderOffer[]; cheapest: number };

function byCountry(offers: ProviderOffer[]): Country[] {
  const map = new Map<string, Country>();
  for (const o of offers) {
    const c = map.get(o.country) ?? { code: o.country, name: o.country_name, offers: [], cheapest: Infinity };
    c.offers.push(o);
    if (o.available && o.min_price > 0) c.cheapest = Math.min(c.cheapest, o.min_price);
    map.set(o.country, c);
  }
  return [...map.values()].filter((c) => Number.isFinite(c.cheapest)).sort((a, b) => a.name.localeCompare(b.name));
}

function roundUp(value: number): number {
  return Math.ceil(value * 100) / 100;
}

export function BuyNumbers({ name, onName }: { name: string; onName: (v: string) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const accounts = useProviderAccounts();
  const [accountId, setAccountId] = useState<string>();
  useEffect(() => {
    const first = accounts.data?.[0];
    if (!accountId && first) setAccountId(first.id);
  }, [accountId, accounts.data]);

  const balance = useProviderBalance(accountId);
  const offers = useProviderOffers(accountId);
  const countries = useMemo(() => byCountry(offers.data ?? []), [offers.data]);

  const [country, setCountry] = useState<string>();
  const [slot, setSlot] = useState<number>();
  // "" is any range on the server; the provider calls these ranges operators.
  const [operator, setOperator] = useState("");
  const [maxPrice, setMaxPrice] = useState<number>(NaN);
  const [target, setTarget] = useState(10);
  const [maxSpend, setMaxSpend] = useState<number>(NaN);
  const [lossLimit, setLossLimit] = useState(5);
  const [noStockWait, setNoStockWait] = useState(120);
  const checker = useCheckerAccount();
  const [checkNumbers, setCheckNumbers] = useState(true);
  const [k, setK] = useState<Knobs>(KNOBS);
  const set = <K extends keyof Knobs>(key: K, value: Knobs[K]) => setK((prev) => ({ ...prev, [key]: value }));

  const chosenCountry = countries.find((c) => c.code === country);
  const chosenSlot = chosenCountry?.offers.find((o) => o.slot === slot);

  // Picking a slot suggests a price cap just above its cheapest price, and a spend cap
  // that covers the target at that price.
  const suggestCaps = (price: number) => {
    const cap = roundUp(price * 1.1);
    setMaxPrice(cap);
    setMaxSpend(roundUp(cap * target * 1.5));
  };
  const pickSlot = (o: ProviderOffer) => {
    setSlot(o.slot);
    setOperator("");
    suggestCaps(o.min_price);
  };
  const pickOperator = (value: string) => {
    setOperator(value);
    const price = chosenSlot?.operators.find((op) => op.operator === value)?.price;
    suggestCaps(price ?? chosenSlot?.min_price ?? maxPrice);
  };
  const pickCountry = (code: string) => {
    setCountry(code);
    const c = countries.find((x) => x.code === code);
    const best = c?.offers.filter((o) => o.available).sort((a, b) => a.min_price - b.min_price)[0];
    if (best) pickSlot(best);
    else setSlot(undefined);
  };

  const affordable = Number.isFinite(maxSpend) && maxPrice > 0 ? Math.floor(maxSpend / maxPrice) : 0;
  const ready =
    !!accountId &&
    !!chosenSlot?.available &&
    maxPrice > 0 &&
    target > 0 &&
    maxSpend >= maxPrice &&
    name.trim().length > 0;

  const start = useMutation({
    mutationFn: () => {
      if (!accountId || !country || slot === undefined) throw new Error("Pick a country and a server first.");
      return unwrap(
        api.POST("/api/runs/buy", {
          body: {
            ...k,
            submit_type: "register",
            prefix: "",
            skip_settled: false,
            otp_poll_interval: 3,
            name: name.trim(),
            provider_account_id: accountId,
            country,
            slot,
            operator: operator || null,
            max_price: maxPrice,
            target,
            max_spend: maxSpend,
            loss_streak_limit: lossLimit,
            no_stock_wait_seconds: noStockWait,
            check_numbers: checkNumbers && !!checker.data,
          },
        }),
      );
    },
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: RUNS_KEY });
      toast.success(`Buying numbers for ${run.provider?.target ?? target} accounts`);
      navigate(`/runs/${run.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const offer = useQuery({
    queryKey: ["auto-email-offer"],
    queryFn: () => unwrap(api.POST("/api/settings/test-key")),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const offerData = offer.data?.ok ? offer.data.auto_email : null;

  if (accounts.isPending) {
    return (
      <div className="grid place-items-center py-16 text-muted-foreground">
        <Loader />
      </div>
    );
  }
  if (!accounts.data?.length) {
    return (
      <List>
        <div className="flex flex-col items-start gap-3 px-(--list-inset) py-6">
          <Text weight="medium">No number provider yet</Text>
          <Text.Hint>Add your provider's API key in Settings, then come back to buy numbers for a run.</Text.Hint>
          <Button asChild>
            <Link to="/settings">Open Settings</Link>
          </Button>
        </div>
      </List>
    );
  }

  return (
    <>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex min-w-0 flex-col gap-5 self-start">
          <List>
            <ListHeader>
              <ListTitle>Numbers to buy</ListTitle>
              {accountId && (
                <Text.Hint className="tabular-nums">
                  Balance {balance.isPending ? "…" : balance.isError ? "unknown" : usd(balance.data?.balance)}
                </Text.Hint>
              )}
            </ListHeader>

            <div className="grid gap-3 px-(--list-inset) pb-3 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Text.Hint size="caption">Provider account</Text.Hint>
                <Select value={accountId} onValueChange={(v) => setAccountId(v)}>
                  <SelectTrigger aria-label="Provider account">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.data.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.label} · {a.provider_title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Text.Hint size="caption">Country</Text.Hint>
                <Combobox
                  aria-label="Country"
                  value={country}
                  onChange={pickCountry}
                  disabled={!countries.length}
                  placeholder={offers.isPending ? "Loading prices…" : "Pick a country"}
                  searchPlaceholder="Search countries"
                  emptyText="No country matches"
                  options={countries.map((c) => ({
                    value: c.code,
                    label: c.name,
                    hint: `from ${usd(c.cheapest)}`,
                    keywords: c.code,
                  }))}
                />
              </div>
            </div>
            {offers.isError && (
              <Text.Danger className="px-(--list-inset) pb-3">{errorMessage(offers.error)}</Text.Danger>
            )}

            {chosenCountry && (
              <div className="border-t">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Server</TableHead>
                      <TableHead className="text-end">Price</TableHead>
                      <TableHead className="text-end">In stock</TableHead>
                      <TableHead className="text-end">Got a code</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chosenCountry.offers.map((o) => (
                      <TableRow
                        key={o.slot}
                        aria-selected={o.slot === slot}
                        className={cn(
                          o.available ? "cursor-pointer" : "opacity-50",
                          o.slot === slot && "bg-primary/10 hover:bg-primary/10",
                        )}
                        onClick={() => o.available && pickSlot(o)}
                      >
                        <TableCell>
                          <button
                            type="button"
                            disabled={!o.available}
                            className="text-start font-medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (o.available) pickSlot(o);
                            }}
                          >
                            {o.label}
                          </button>
                        </TableCell>
                        <TableCell className="text-end tabular-nums">
                          {o.available
                            ? o.min_price === o.max_price
                              ? usd(o.min_price)
                              : `${usd(o.min_price)}–${usd(o.max_price)}`
                            : "none now"}
                        </TableCell>
                        <TableCell className="text-end tabular-nums">{o.stock.toLocaleString()}</TableCell>
                        <TableCell className="text-end tabular-nums">
                          {o.success_rate === null ? "—" : `${Math.round(o.success_rate * 100)}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {!!chosenSlot?.operator_choice && chosenSlot.operators.length > 0 && (
              <div className="flex flex-col gap-2 border-t px-(--list-inset) py-3">
                <Text.Hint size="caption">Number range</Text.Hint>
                <Combobox
                  aria-label="Number range"
                  value={operator}
                  onChange={pickOperator}
                  placeholder="Any range"
                  searchPlaceholder="Search ranges"
                  emptyText="No range matches"
                  options={[
                    { value: "", label: "Any range", hint: `${chosenSlot.stock.toLocaleString()} in stock` },
                    ...chosenSlot.operators.map((op) => ({
                      value: op.operator,
                      label: op.operator,
                      hint: `${usd(op.price)} · ${op.stock.toLocaleString()} left`,
                    })),
                  ]}
                />
                <Text.Hint size="caption">
                  Buy only numbers starting this way. The whole server is used otherwise.
                </Text.Hint>
              </div>
            )}
          </List>

          <List className="pb-2">
            <ListHeader>
              <ListTitle>Limits</ListTitle>
            </ListHeader>
            <NumberRow
              label="Accounts to register"
              hint="Numbers are bought until this many register"
              value={target}
              min={1}
              max={100000}
              step={1}
              onChange={setTarget}
            />
            <NumberRow
              label="Max price per number, USD"
              hint="Cheaper numbers on the server are used first"
              value={maxPrice}
              min={0.01}
              max={100}
              step={0.01}
              onChange={setMaxPrice}
            />
            <NumberRow
              label="Max spend, USD"
              hint="Refunded numbers don't count"
              value={maxSpend}
              min={0.01}
              max={100000}
              step={0.5}
              onChange={setMaxSpend}
            />
            <NumberRow
              label="Give up after no stock, seconds"
              hint="Keep asking this long when the provider has none; 0 stops at once"
              value={noStockWait}
              min={0}
              max={1000000}
              step={10}
              onChange={setNoStockWait}
            />
            <NumberRow
              label="Stop after failed paid numbers"
              hint="In a row, where the code came so there's no refund"
              value={lossLimit}
              min={1}
              max={1000}
              step={1}
              onChange={setLossLimit}
            />
            <NumberRow
              label="Stop after repeated failures"
              hint="Set 0 to disable"
              value={k.failure_limit}
              min={0}
              max={1000}
              step={1}
              onChange={(v) => set("failure_limit", v)}
            />
          </List>
        </div>

        <List className="self-start pb-2">
          <ListHeader>
            <ListTitle>Settings</ListTitle>
          </ListHeader>
          <Field label="Run name">
            {(id) => <Input id={id} value={name} maxLength={200} onChange={(e) => onName(e.target.value)} />}
          </Field>
          <NumberRow
            label="Numbers at once"
            value={k.concurrency}
            min={1}
            max={100}
            step={1}
            onChange={(v) => set("concurrency", v)}
          />
          <NumberRow
            label="Tries per number"
            hint="On the same number, for failures that may pass next time"
            value={k.max_attempts}
            min={1}
            max={10}
            step={1}
            onChange={(v) => set("max_attempts", v)}
          />
          <NumberRow
            label="Stagger, seconds"
            hint="Random pause before each number"
            value={k.stagger_seconds}
            min={0}
            max={600}
            step={0.5}
            onChange={(v) => set("stagger_seconds", v)}
          />
          <NumberRow
            label="Codes per second"
            hint="The server's own limit still applies"
            value={k.send_rate_per_second}
            min={0.05}
            max={50}
            step={0.05}
            onChange={(v) => set("send_rate_per_second", v)}
          />
          <NumberRow
            label="Code wait, seconds"
            hint="An SMS to a bought number can be slow"
            value={k.otp_poll_timeout}
            min={5}
            max={270}
            step={1}
            onChange={(v) => set("otp_poll_timeout", v)}
          />
          <CheckRow
            label="Auto email"
            hint={
              offerData?.available
                ? `Rent the email Telegram asks for, ${offerData.price ?? 0} off each account's price`
                : "Rent the email Telegram asks for, when offered"
            }
            checked={k.auto_email}
            onChange={(v) => set("auto_email", v)}
          />
          <CheckRow
            label="Check each number first"
            hint={
              checker.data
                ? "Numbers that already have a Telegram account are given back unused, for a refund"
                : checker.isPending
                  ? "Looking for your number checker…"
                  : "Add a number checker in Settings to use this"
            }
            checked={checkNumbers && !!checker.data}
            disabled={!checker.data}
            onChange={setCheckNumbers}
          />
        </List>
      </div>

      <List>
        <ListItem>
          <ListItemContent>
            <Text weight="medium">
              {chosenSlot
                ? `${chosenCountry?.name}, ${chosenSlot.label}${operator ? ` · ${operator}` : ""}: ${target} ${
                    target === 1 ? "account" : "accounts"
                  }`
                : "Pick a country and a server"}
            </Text>
            <Text.Hint size="caption" className="tabular-nums">
              {chosenSlot && maxPrice > 0 && Number.isFinite(maxSpend)
                ? `At most ${usd(maxPrice)} a number and ${usd(maxSpend)} in all: room for ${affordable} paid ${
                    affordable === 1 ? "number" : "numbers"
                  }. Numbers that fail before their code are refunded and replaced.`
                : "Set the limits above."}
              {maxSpend < maxPrice ? " The spend cap is below the price of one number." : ""}
            </Text.Hint>
          </ListItemContent>
          <ListItemTrailing>
            <Button disabled={!ready} loading={start.isPending} onClick={() => start.mutate()}>
              Buy and register
            </Button>
          </ListItemTrailing>
        </ListItem>
      </List>
    </>
  );
}
